import { decrypt } from '@/common/securityUtils';
import {
  emailRegex,
  instagramUrlRegex,
  landlineNumberRegex,
  latitudeRegex,
  lineUrlRegex,
  longitudeRegex,
  mobileNumberRegex,
  nameValidationRegex,
  tiktokUrlRegex,
  twitterUrlRegex,
  websiteUrlRegex,
} from '@/constants/regex';
import { isGoogleMapsUrl } from '@/lib/isGoogleMapsUrl';
import { hasUploadValue, optionalUploadValueSchema } from '@/lib/uploadSchema';
import { z } from 'zod';
import { BUSINESS_TYPES } from './businessType';
import { INDENTIFICATION_TYPES } from './identificationType';

export const formSchema = z
  .object({
    coverImage: optionalUploadValueSchema,
    profileImage: optionalUploadValueSchema,
    storeName: z
      .string()
      .min(1, { message: 'common.form.validation.required' })
      .refine((val) => nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),
    storeNameFurigana: z
      .string()
      .min(1, 'common.form.validation.required')
      .refine((val) => nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),

    groupName: z.string().refine((val) => val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
    description: z.string().min(1, 'common.form.validation.required'),
    businessType: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    ceoFullName: z
      .string()
      .min(1, 'common.form.validation.required')
      .refine((val) => nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),
    ceoFurigana: z
      .string()
      .min(1, 'common.form.validation.required')
      .refine((val) => nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),
    companyName: z.string().optional(),
    companyNameFurigana: z.string().optional(),
    companyAddress: z.string().refine((val) => val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
    landlineNumber: z.string().min(1, 'common.form.validation.required').regex(landlineNumberRegex, {
      message: 'common.form.validation.correctMobileNumber',
    }),
    mobileNumber: z.string().min(1, 'common.form.validation.required').regex(mobileNumberRegex, {
      message: 'common.form.validation.invalidLandlineNumber',
    }),
    emailAddress: z
      .string()
      .min(1, { message: 'common.form.validation.required' })
      .refine((val) => emailRegex.test(val), { message: 'common.form.validation.invalidEmail' }),
    username: z.string().optional(),
    longitude: z
      .string()
      .trim()
      .refine((val) => val === '' || longitudeRegex.test(val), {
        message: 'common.form.validation.invalid',
      })
      .optional(),
    latitude: z
      .string()
      .trim()
      .refine((val) => val === '' || latitudeRegex.test(val))
      .optional(),

    postalCode: z.string().min(1, 'common.form.validation.required'),
    prefecture: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    city: z
      .string()
      .min(1, 'common.form.validation.required')
      .refine((val) => nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),
    street: z
      .string()
      .min(1, 'common.form.validation.required')
      .refine((val) => nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),
    buildingNo: z.string().refine((val) => val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
    room: z.string().optional(),
    identificationType: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    kycFront: optionalUploadValueSchema,
    kycBack: optionalUploadValueSchema,
    passportImage: optionalUploadValueSchema,
    insuranceCardImage: optionalUploadValueSchema,
    businessCertificateImage: optionalUploadValueSchema,
    corporateRegistryImage: optionalUploadValueSchema,
    businessLicenseNumber: z.string().optional(),
    licenseIssueDate: z.string().optional(),
    workingTimeFrom: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    workingTimeTo: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    lastEntryTime: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    holidays: z.string().optional(),
    otherHolidays: z.array(z.string()).optional().default([]),
    tax: z.string().optional(),
    closingDate: z.string().optional(),
    instagram: z.string().regex(instagramUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
    tiktok: z.string().regex(tiktokUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
    line: z.string().regex(lineUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
    twitter: z.string().regex(twitterUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
    website: z.string().regex(websiteUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
    googleMap: z
      .string()
      .trim()
      .min(1, 'common.form.validation.required')
      .refine(isGoogleMapsUrl, 'common.form.validation.enterValidUrl'),
  })
  .superRefine((data, ctx) => {
    const idType = data.identificationType;
    const bizType = data.businessType;
    const isPassportInsurance = decrypt(idType) === INDENTIFICATION_TYPES.PASSPORT_INSURANCE;
    const isCorporation = bizType === BUSINESS_TYPES.CORPORATION;
    if (!hasUploadValue(data.businessCertificateImage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['businessCertificateImage'],
        message: 'common.form.validation.required',
      });
    }
    if (isPassportInsurance) {
      if (!hasUploadValue(data.passportImage)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['passportImage'],
          message: 'common.form.validation.required',
        });
      }
      if (!hasUploadValue(data.insuranceCardImage)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['insuranceCardImage'],
          message: 'common.form.validation.required',
        });
      }
    } else {
      if (!hasUploadValue(data.kycFront)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['kycFront'],
          message: 'common.form.validation.required',
        });
      }
      if (!hasUploadValue(data.kycBack)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['kycBack'],
          message: 'common.form.validation.required',
        });
      }
    }
    if (isCorporation) {
      if (!data.companyName || data.companyName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyName'],
          message: 'common.form.validation.required',
        });
      } else if (!nameValidationRegex.test(data.companyName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyName'],
          message: 'common.form.validation.nameOnly',
        });
      }

      if (!data.companyNameFurigana || data.companyNameFurigana.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyNameFurigana'],
          message: 'common.form.validation.required',
        });
      } else if (!nameValidationRegex.test(data.companyNameFurigana)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyNameFurigana'],
          message: 'common.form.validation.nameOnly',
        });
      }

      if (!hasUploadValue(data.corporateRegistryImage)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['corporateRegistryImage'],
          message: 'common.form.validation.required',
        });
      }
    }
  });

export type PremiumStoreFormValues = z.infer<typeof formSchema>;
